/**
 * Decides when a response or runtime error is a stale deploy asset.
 * The service worker mirrors the response checks in public/sw.js.
 * Keep the two copies aligned: a normal application exception whose
 * stack points at /static/js must not be treated as a broken deploy.
 */

export const ASSET_RECOVERY_KEY = 'portfolio-asset-recovery';

export function isLocalDevelopmentHost(hostname = '') {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost')
  );
}

export function isHtmlContentType(contentType = '') {
  return contentType.toLowerCase().includes('text/html');
}

/**
 * A hashed JS/CSS response is usable only when it is a real 200 asset.
 * Netlify's SPA fallback returns index.html with status 200 for missing
 * files, which browsers will otherwise parse as JavaScript.
 */
export function isUsableStaticAsset({ status, type, contentType = '', url = '' } = {}) {
  if (status !== 200) return false;
  if (type && type !== 'basic' && type !== 'default') return false;
  if (isHtmlContentType(contentType)) return false;

  const path = url.split('?')[0];
  const mime = contentType.toLowerCase();
  if (path.endsWith('.js')) {
    return mime.includes('javascript') || mime.includes('ecmascript');
  }
  if (path.endsWith('.css')) {
    return mime.includes('text/css');
  }
  return true;
}

function isStaticAssetUrl(url) {
  return /\/static\/.+\.(?:js|css)(?:[?#]|$)/.test(url || '');
}

export function isStaleAssetFailure({
  message = '',
  name = '',
  filename = '',
  targetUrl = '',
  targetTag = '',
} = {}) {
  if (name === 'ChunkLoadError') return true;

  const isStaticResource = isStaticAssetUrl(filename) || isStaticAssetUrl(targetUrl);
  const tag = targetTag.toUpperCase();
  const isAssetElement = tag === 'SCRIPT' || tag === 'LINK';

  if (isAssetElement && isStaticResource) return true;

  if (
    /Loading chunk|Loading CSS chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      message
    )
  ) {
    return true;
  }

  if (/SyntaxError|Unexpected token\s*</i.test(message) && isStaticResource) return true;

  return false;
}

/**
 * pending: a reload is already in flight.
 * attempted: the previous reload did not recover, so show fallback UI.
 * anything else: try one cache-clearing reload.
 */
export function recoveryDecision(currentValue) {
  if (currentValue === 'pending') return 'wait';
  if (currentValue === 'attempted') return 'fallback';
  return 'recover';
}
